import React, { useState, useEffect } from 'react'
import { useRecordContext, useRefresh, useNotify } from 'react-admin'
import { Card, CardContent, Typography, TextField, Button, Stack, Paper, Box, Alert } from '@mui/material'
import { API_URL } from '../../services/settings'

const LeadNotes = () => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()
  const [noteContent, setNoteContent] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [notes, setNotes] = useState([])

  // Initialize notes from record
  useEffect(() => {
    if (record?.notes_list) {
      setNotes(record.notes_list)
    }
  }, [record])

  if (!record) return null

  const handleAddNote = async () => {
    if (!noteContent.trim()) return

    setLoading(true)
    setError(null)

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        setError('Not authenticated. Please log in again.')
        setLoading(false)
        return
      }

      const url = `${API_URL}/leads/${record.id}/notes`

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content: noteContent }),
      })

      if (response.ok) {
        const data = await response.json()

        // Add the new note to the local state immediately
        if (data.data) {
          const newNote = data.data
          setNotes([newNote, ...notes]) // Add to beginning (newest first)
        }

        setNoteContent('')
        notify('Note added successfully', { type: 'success' })

        // Also refresh to ensure backend state is in sync
        setTimeout(() => {
          refresh()
        }, 500)
      } else {
        const errorData = await response.json().catch(() => ({}))
        const errorMsg = errorData.message || response.statusText
        setError(`Failed to add note: ${errorMsg}`)
      }
    } catch (error) {
      setError(`Error: ${error.message}`)
    } finally {
      setLoading(false)
    }
  }

  return (
    <Card sx={{ mt: 3 }}>
      <CardContent>
        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
          Notes ({notes.length})
        </Typography>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Add Note Section */}
        <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            rows={3}
            placeholder="Add a note..."
            value={noteContent}
            onChange={(e) => setNoteContent(e.target.value)}
            disabled={loading}
            sx={{ mb: 1 }}
          />
          <Button
            variant="contained"
            onClick={handleAddNote}
            disabled={!noteContent.trim() || loading}
            size="small"
          >
            {loading ? 'Adding...' : 'Add Note'}
          </Button>
        </Box>

        {/* Display Notes */}
        {notes && notes.length > 0 ? (
          <Stack spacing={2}>
            {notes.map((note, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: 'action.hover' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 'bold', color: 'text.primary' }}>
                    {note.author}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {new Date(note.timestamp).toLocaleString()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.primary' }}>{note.content}</Typography>
              </Paper>
            ))}
          </Stack>
        ) : (
          <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
            No notes yet
          </Typography>
        )}
      </CardContent>
    </Card>
  )
}

export default LeadNotes
